import { FormEvent, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Search } from "lucide-react";
import { api, type AuditLog, type Hotel } from "../api/client";

export function AuditPage() {
  const [filters, setFilters] = useState({ action: "", hotelId: "", from: "", to: "" });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const queryString = useMemo(() => auditQuery(filters), [filters]);
  const logs = useQuery({
    queryKey: ["audit-logs", queryString],
    queryFn: () => api<AuditLog[]>(`/api/admin/audit-logs${queryString ? `?${queryString}` : ""}`)
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setFilters({
      action: String(form.get("action") || "").trim(),
      hotelId: String(form.get("hotelId") || ""),
      from: datetimeValue(form, "from"),
      to: datetimeValue(form, "to")
    });
  }

  function exportCsv() {
    const href = `/api/admin/audit-logs/export.csv${queryString ? `?${queryString}` : ""}`;
    window.open(href, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card">
        <div>
          <span className="panel-eyebrow left">Audit Trail</span>
          <h2>Pregled i export sigurnosnih i admin događaja.</h2>
          <p>Audit log je tenant-scoped, read-only i export se posebno audit-loguje.</p>
        </div>
        <div className="object-brand-card">
          <FileText size={34} />
          <strong>{logs.data?.length ?? 0} zapisa</strong>
          <span>zadnjih 200 u listi</span>
        </div>
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Filteri</h2>
            <p>CSV export uzima do 5000 zapisa za iste filtere.</p>
          </div>
          <button type="button" onClick={exportCsv}><Download size={16} /> Export CSV</button>
        </div>
        <form className="inline-form" onSubmit={submit}>
          <input name="action" placeholder="Action, npr. login" />
          <select name="hotelId">
            <option value="">Svi scope-ovi</option>
            {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
          </select>
          <input name="from" type="datetime-local" />
          <input name="to" type="datetime-local" />
          <button type="submit"><Search size={16} /> Primijeni</button>
        </form>
      </section>

      <section className="panel card">
        <div className="table-shell">
          <table>
            <thead>
              <tr>
                <th>Vrijeme</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Actor</th>
                <th>Hotel</th>
                <th>Metadata</th>
              </tr>
            </thead>
            <tbody>
              {(logs.data ?? []).map((log) => (
                <tr key={log.id}>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                  <td><span className="status">{log.action}</span></td>
                  <td>{log.entityType}<span>{log.entityId ?? "-"}</span></td>
                  <td>{log.actorUserId ?? "system"}</td>
                  <td>{hotelName(log.hotelId, hotels.data ?? [])}</td>
                  <td><code>{JSON.stringify(log.metadata ?? {})}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {logs.isError && <p className="form-error">Audit log nije dostupan za trenutni nalog.</p>}
      </section>
    </div>
  );
}

function auditQuery(filters: { action: string; hotelId: string; from: string; to: string }) {
  const params = new URLSearchParams();
  if (filters.action) params.set("action", filters.action);
  if (filters.hotelId) params.set("hotelId", filters.hotelId);
  if (filters.from) params.set("from", new Date(filters.from).toISOString());
  if (filters.to) params.set("to", new Date(filters.to).toISOString());
  return params.toString();
}

function datetimeValue(form: FormData, key: string) {
  const value = String(form.get(key) || "");
  return value ? value : "";
}

function hotelName(hotelId: string | null | undefined, hotels: Hotel[]) {
  if (!hotelId) return "org/system";
  return hotels.find((hotel) => hotel.id === hotelId)?.name ?? hotelId;
}
